import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Tooltip from "../../common/Tooltip/Tooltip";
import TextFormatFloatingMenu from "../textToolbar/TextFormatFloatingMenu";
import {
  OVERLAY_COLOR,
  OVERLAY_POSITION,
  useWatermarkToolbarOverlay,
} from "../../../features/tools/watermark/Popup/WatermarkToolbarOverlayContext";
import {
  DEFAULT_IMAGE_FORMAT,
  IMAGE_EFFECT_OPTIONS,
  IMAGE_FORMAT_TOOLS,
  IMAGE_FORMAT_TOOL_SETS,
} from "./imageFormatModel";
import { resolveSizeMenuOptions } from "../textToolbar/textFormatModel";
import {
  DEFAULT_CROP_RECT,
  IMAGE_CROP_ASPECTS,
  fitCropRectForAspectId,
  isFullCropRect,
  normalizeCropRect,
} from "./imageCropModel";
import "../textToolbar/TextFormatToolbar.css";
import "./ImageFormatToolbar.css";

const TftTipContext = createContext(null);

const TftTip = ({ label, children }) => {
  const toolbarRef = useContext(TftTipContext);
  return label ? (
    <Tooltip
      content={label}
      position="bottom"
      size="menu"
      gap={2}
      edgeRef={toolbarRef}
      flip={false}
    >
      {children}
    </Tooltip>
  ) : (
    children
  );
};

const FlipHIcon = () => (
  <svg className="tft-icon" viewBox="0 0 18 18" aria-hidden="true">
    <path
      d="M9 2.5v13M4 6.5L2.5 9 4 11.5M14 6.5L15.5 9 14 11.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FlipVIcon = () => (
  <svg className="tft-icon" viewBox="0 0 18 18" aria-hidden="true">
    <path
      d="M2.5 9h13M6.5 4L9 2.5 11.5 4M6.5 14L9 15.5 11.5 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StrokeIcon = () => (
  <svg className="tft-icon" viewBox="0 0 18 18" aria-hidden="true">
    <rect
      x="3.5"
      y="3.5"
      width="11"
      height="11"
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const CornerIcon = () => (
  <svg className="tft-icon" viewBox="0 0 18 18" aria-hidden="true">
    <path
      d="M4 14V8a4 4 0 0 1 4-4h6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Generic image formatting toolbar — host supplies value/onChange/tools.
 */
const ImageFormatToolbar = ({
  value: valueProp = {},
  onChange = () => {},
  tools = IMAGE_FORMAT_TOOL_SETS.default,
  disabled = false,
  className = "",
  sizeConfig = {
    min: 0,
    max: 24,
    formatDisplay: (v) => String(Math.round(Number(v) * 10)),
    parseDisplay: (display) => Number(display) / 10,
  },
  /** Host-provided: run logo/image background removal */
  onBgRemove = null,
  bgRemoveBusy = false,
  bgRemoveDone = false,
}) => {
  const value = useMemo(
    () => ({ ...DEFAULT_IMAGE_FORMAT, ...valueProp }),
    [valueProp]
  );
  const overlayCtx = useWatermarkToolbarOverlay();
  const useLeftPanelOverlay = Boolean(overlayCtx);
  const toolbarRef = useRef(null);

  const [strokeOpen, setStrokeOpen] = useState(false);
  const [cornerOpen, setCornerOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [flipOpen, setFlipOpen] = useState(false);
  const [effectOpen, setEffectOpen] = useState(false);
  const [opacityOpen, setOpacityOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);

  const strokeBtnRef = useRef(null);
  const cornerBtnRef = useRef(null);
  const cropBtnRef = useRef(null);
  const flipBtnRef = useRef(null);
  const effectBtnRef = useRef(null);
  const opacityBtnRef = useRef(null);
  const sizeControlRef = useRef(null);
  const sizeInputRef = useRef(null);
  const skipSizeCommitRef = useRef(false);

  const toolSet = useMemo(() => new Set(tools), [tools]);
  const has = (id) => toolSet.has(id);

  const patch = (partial) => onChange({ ...value, ...partial });

  const closeMenus = (except = "") => {
    if (except !== "stroke") setStrokeOpen(false);
    if (except !== "corner") setCornerOpen(false);
    if (except !== "crop") setCropOpen(false);
    if (except !== "flip") setFlipOpen(false);
    if (except !== "effect") setEffectOpen(false);
    if (except !== "opacity") setOpacityOpen(false);
    if (except !== "size") setSizeOpen(false);
    if (
      except !== "color" &&
      useLeftPanelOverlay &&
      overlayCtx?.isColorOpen
    ) {
      overlayCtx.closeOverlay();
    }
  };

  const formatDisplay =
    sizeConfig.formatDisplay ?? ((v) => String(Math.round(Number(v) * 10)));
  const parseSizeDisplay =
    sizeConfig.parseDisplay ?? ((display) => Number(display) / 10);
  const sizeMin = sizeConfig.min ?? 0;
  const sizeMax = sizeConfig.max ?? 24;
  const sizeMenuOptions = useMemo(
    () => resolveSizeMenuOptions(sizeConfig),
    [sizeConfig]
  );

  const sizeDisplay = formatDisplay(Number(value.size) || 0);
  const [sizeDraft, setSizeDraft] = useState(sizeDisplay);
  const [sizeInputFocused, setSizeInputFocused] = useState(false);

  useEffect(() => {
    if (!sizeInputFocused) setSizeDraft(sizeDisplay);
  }, [sizeDisplay, sizeInputFocused]);

  const clampInternalSize = (internal) =>
    Math.max(sizeMin, Math.min(sizeMax, internal));

  const applyDisplaySize = (displayValue) => {
    patch({ size: clampInternalSize(parseSizeDisplay(displayValue)) });
  };

  const stepDisplaySize = (delta) => {
    const idx = sizeMenuOptions.findIndex((o) => o.display === sizeDisplay);
    if (idx >= 0) {
      const next = sizeMenuOptions[idx + delta];
      if (next) {
        patch({ size: next.internal });
        return;
      }
    }
    const current = Number(sizeDisplay);
    const base = Number.isFinite(current)
      ? current
      : Number(sizeMenuOptions[0]?.display ?? 0);
    applyDisplaySize(base + delta);
  };

  const commitSizeDraft = (raw) => {
    const cleaned = String(raw ?? "").trim();
    if (!cleaned) {
      setSizeDraft(sizeDisplay);
      return;
    }
    const n = Number(cleaned);
    if (!Number.isFinite(n)) {
      setSizeDraft(sizeDisplay);
      return;
    }
    applyDisplaySize(n);
  };

  const openSizeEditor = () => {
    if (disabled) return;
    closeMenus("size");
    setSizeOpen(true);
    window.requestAnimationFrame(() => {
      const input = sizeInputRef.current;
      if (!input) return;
      input.focus();
      input.select();
    });
  };

  useEffect(() => {
    if (
      !strokeOpen &&
      !cornerOpen &&
      !cropOpen &&
      !flipOpen &&
      !opacityOpen &&
      !sizeOpen
    ) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "Escape") closeMenus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    strokeOpen,
    cornerOpen,
    cropOpen,
    flipOpen,
    opacityOpen,
    sizeOpen,
  ]);

  const colorActive = useLeftPanelOverlay ? overlayCtx.isColorOpen : false;
  const positionActive = useLeftPanelOverlay
    ? overlayCtx.isPositionOpen
    : false;

  const strokeWidth = Math.max(0, Math.min(40, Number(value.strokeWidth) || 0));
  const cornerRadius = Math.max(
    0,
    Math.min(50, Number(value.cornerRadius) || 0)
  );
  const cropInset = Math.max(0, Math.min(40, Number(value.cropInset) || 0));
  const cropAspect = value.cropAspect || "custom";
  const cropActive = Boolean(value.cropActive);
  const cropRect = value.cropRect
    ? normalizeCropRect(value.cropRect)
    : null;
  const hasCrop =
    Boolean(cropRect && !isFullCropRect(cropRect)) ||
    cropInset > 0 ||
    cropActive;

  const beginCrop = (aspectId) => {
    const current = cropRect || { ...DEFAULT_CROP_RECT };
    const nextRect = fitCropRectForAspectId(
      aspectId,
      value.naturalWidth,
      value.naturalHeight,
      current
    );
    patch({
      cropAspect: aspectId,
      cropActive: true,
      cropRect: nextRect,
      cropSessionStartRect: current,
      cropInset: 0,
    });
    setCropOpen(false);
  };

  const toolbarClassName = `text-format-toolbar image-format-toolbar${
    className ? ` ${className}` : ""
  }${disabled ? " is-disabled" : ""}`;

  return (
    <TftTipContext.Provider value={toolbarRef}>
      <div
        ref={toolbarRef}
        className={toolbarClassName}
        role="toolbar"
        aria-label="Image formatting"
      >
        <div className="text-format-toolbar__scroll">
          {has(IMAGE_FORMAT_TOOLS.BG_REMOVER) ? (
            <TftTip
              label={
                bgRemoveDone
                  ? "Background already removed"
                  : "Remove logo background"
              }
            >
              <button
                type="button"
                className={`tft-toggle-btn tft-position-btn tft-bg-remove-btn${
                  bgRemoveDone ? " is-active" : ""
                }${bgRemoveBusy ? " is-busy" : ""}`}
                disabled={disabled || bgRemoveBusy || bgRemoveDone || !onBgRemove}
                aria-label="BG Remover"
                aria-busy={bgRemoveBusy}
                onClick={() => {
                  closeMenus();
                  onBgRemove?.();
                }}
              >
                <span className="tft-bg-remove-btn__label">
                  {bgRemoveBusy ? "Removing…" : "BG Remover"}
                </span>
                {!bgRemoveBusy ? (
                  <svg
                    className="tft-bg-remove-btn__crown"
                    viewBox="0 0 16 16"
                    width="12"
                    height="12"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M2.2 12.2h11.6l-1.05-6.1-2.85 2.35L8 4.4 6.1 8.45 3.25 6.1 2.2 12.2z"
                      fill="currentColor"
                    />
                    <path
                      d="M2.2 12.2h11.6v1.2H2.2z"
                      fill="currentColor"
                      opacity="0.85"
                    />
                  </svg>
                ) : null}
              </button>
            </TftTip>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.SIZE) ? (
            <div
              ref={sizeControlRef}
              className={`tft-size-control${sizeOpen ? " is-open" : ""}`}
              role="group"
              aria-label="Image size"
            >
              <TftTip label="Decrease size">
                <button
                  type="button"
                  className="tft-size-step"
                  disabled={disabled}
                  aria-label="Decrease size"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    stepDisplaySize(-1);
                  }}
                >
                  −
                </button>
              </TftTip>
              <TftTip label="Size">
                <div
                  className="tft-size-field"
                  onMouseDown={(e) => {
                    if (e.target.closest(".tft-size-input")) return;
                    e.preventDefault();
                    openSizeEditor();
                  }}
                >
                  <input
                    ref={sizeInputRef}
                    type="text"
                    inputMode="numeric"
                    className="tft-size-input"
                    value={sizeDraft}
                    disabled={disabled}
                    aria-label="Image size value"
                    aria-expanded={sizeOpen}
                    aria-haspopup="listbox"
                    onFocus={() => {
                      setSizeInputFocused(true);
                      if (!sizeOpen) setSizeOpen(true);
                    }}
                    onChange={(e) => setSizeDraft(e.target.value)}
                    onBlur={(e) => {
                      setSizeInputFocused(false);
                      if (skipSizeCommitRef.current) {
                        skipSizeCommitRef.current = false;
                        return;
                      }
                      commitSizeDraft(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.currentTarget.blur();
                        setSizeOpen(false);
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setSizeDraft(sizeDisplay);
                        e.currentTarget.blur();
                        setSizeOpen(false);
                      }
                    }}
                  />
                </div>
              </TftTip>
              <TftTip label="Increase size">
                <button
                  type="button"
                  className="tft-size-step"
                  disabled={disabled}
                  aria-label="Increase size"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    stepDisplaySize(1);
                  }}
                >
                  +
                </button>
              </TftTip>
              <TextFormatFloatingMenu
                open={sizeOpen}
                anchorRef={sizeControlRef}
                onClose={() => setSizeOpen(false)}
                className="tft-menu tft-menu--size tft-menu--floating"
                align="center"
              >
                <ul role="listbox" aria-label="Size">
                  {sizeMenuOptions.map(({ display, internal }) => (
                    <li key={display}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={display === sizeDisplay}
                        className={`tft-menu__item tft-menu__item--size${
                          display === sizeDisplay ? " is-active" : ""
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          skipSizeCommitRef.current = true;
                          patch({ size: internal });
                          setSizeDraft(display);
                          setSizeOpen(false);
                          sizeInputRef.current?.blur();
                        }}
                      >
                        {display}
                      </button>
                    </li>
                  ))}
                </ul>
              </TextFormatFloatingMenu>
            </div>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.COLOR) && useLeftPanelOverlay ? (
            <TftTip label="Color">
              <button
                type="button"
                className={`tft-color-btn${colorActive ? " is-open" : ""}`}
                disabled={disabled}
                aria-label="Image color"
                aria-expanded={colorActive}
                onClick={() => {
                  closeMenus("color");
                  overlayCtx.openOverlay(OVERLAY_COLOR);
                }}
              >
                <span className="tft-color-btn__letter">A</span>
                <span
                  className={`tft-color-btn__bar${
                    value.tintColor ? "" : " tft-color-btn__bar--rainbow"
                  }`}
                  style={
                    value.tintColor
                      ? { background: value.tintColor }
                      : undefined
                  }
                  aria-hidden="true"
                />
              </button>
            </TftTip>
          ) : null}

          <div className="tft-divider" aria-hidden="true" />

          {has(IMAGE_FORMAT_TOOLS.STROKE) ? (
            <div className="tft-group">
              <TftTip label="Stroke">
                <button
                  ref={strokeBtnRef}
                  type="button"
                  className={`tft-toggle-btn${strokeOpen ? " is-open" : ""}${
                    strokeWidth > 0 ? " is-active" : ""
                  }`}
                  disabled={disabled}
                  aria-label="Stroke"
                  aria-expanded={strokeOpen}
                  onClick={() => {
                    closeMenus("stroke");
                    setStrokeOpen((v) => !v);
                  }}
                >
                  <StrokeIcon />
                </button>
              </TftTip>
              <TextFormatFloatingMenu
                open={strokeOpen}
                anchorRef={strokeBtnRef}
                onClose={() => setStrokeOpen(false)}
                className="tft-popover tft-popover--opacity tft-menu--floating"
              >
                <div className="tft-slider-field">
                  <div className="tft-slider-field__head">
                    <span className="tft-slider-field__label">Stroke</span>
                    <div className="tft-slider-field__value-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="tft-slider-field__input"
                        value={Math.round(strokeWidth)}
                        disabled={disabled}
                        aria-label="Stroke width"
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) {
                            patch({
                              strokeWidth: Math.max(0, Math.min(40, n)),
                            });
                          }
                        }}
                      />
                      <span className="tft-slider-field__suffix">px</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    className="tft-slider"
                    min={0}
                    max={40}
                    step={1}
                    value={strokeWidth}
                    disabled={disabled}
                    aria-label="Stroke width"
                    onChange={(e) =>
                      patch({ strokeWidth: Number(e.target.value) })
                    }
                  />
                </div>
              </TextFormatFloatingMenu>
            </div>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.ROUND_CORNER) ? (
            <div className="tft-group">
              <TftTip label="Round corner">
                <button
                  ref={cornerBtnRef}
                  type="button"
                  className={`tft-toggle-btn${cornerOpen ? " is-open" : ""}${
                    cornerRadius > 0 ? " is-active" : ""
                  }`}
                  disabled={disabled}
                  aria-label="Round corner"
                  aria-expanded={cornerOpen}
                  onClick={() => {
                    closeMenus("corner");
                    setCornerOpen((v) => !v);
                  }}
                >
                  <CornerIcon />
                </button>
              </TftTip>
              <TextFormatFloatingMenu
                open={cornerOpen}
                anchorRef={cornerBtnRef}
                onClose={() => setCornerOpen(false)}
                className="tft-popover tft-popover--opacity tft-menu--floating"
              >
                <div className="tft-slider-field">
                  <div className="tft-slider-field__head">
                    <span className="tft-slider-field__label">Corners</span>
                    <div className="tft-slider-field__value-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="tft-slider-field__input"
                        value={Math.round(cornerRadius)}
                        disabled={disabled}
                        aria-label="Corner radius"
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) {
                            patch({
                              cornerRadius: Math.max(0, Math.min(50, n)),
                            });
                          }
                        }}
                      />
                      <span className="tft-slider-field__suffix">px</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    className="tft-slider"
                    min={0}
                    max={50}
                    step={1}
                    value={cornerRadius}
                    disabled={disabled}
                    aria-label="Corner radius"
                    onChange={(e) =>
                      patch({ cornerRadius: Number(e.target.value) })
                    }
                  />
                </div>
              </TextFormatFloatingMenu>
            </div>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.CROP) ? (
            <div className="tft-group">
              <TftTip label="Crop">
                <button
                  ref={cropBtnRef}
                  type="button"
                  className={`tft-toggle-btn tft-position-btn${
                    cropOpen ? " is-open" : ""
                  }${hasCrop ? " is-active" : ""}`}
                  disabled={disabled}
                  aria-label="Crop"
                  aria-expanded={cropOpen}
                  aria-haspopup="menu"
                  onClick={() => {
                    closeMenus("crop");
                    setCropOpen((v) => !v);
                  }}
                >
                  Crop
                </button>
              </TftTip>
              <TextFormatFloatingMenu
                open={cropOpen}
                anchorRef={cropBtnRef}
                onClose={() => setCropOpen(false)}
                className="tft-menu tft-menu--crop tft-menu--floating"
                style={{ minWidth: 188, width: "max-content", maxWidth: "none" }}
              >
                <ul role="menu" aria-label="Aspect ratio">
                  {IMAGE_CROP_ASPECTS.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={cropAspect === opt.id}
                        className={`tft-menu__item${
                          cropAspect === opt.id ? " is-active" : ""
                        }`}
                        onClick={() => beginCrop(opt.id)}
                      >
                        <span className="tft-menu__crop-short">{opt.short}</span>
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </TextFormatFloatingMenu>
            </div>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.FLIP) ? (
            <div className="tft-group">
              <TftTip label="Flip">
                <button
                  ref={flipBtnRef}
                  type="button"
                  className={`tft-toggle-btn tft-position-btn${
                    flipOpen ? " is-open" : ""
                  }${value.flipH || value.flipV ? " is-active" : ""}`}
                  disabled={disabled}
                  aria-label="Flip"
                  aria-expanded={flipOpen}
                  aria-haspopup="menu"
                  onClick={() => {
                    closeMenus("flip");
                    setFlipOpen((v) => !v);
                  }}
                >
                  Flip
                </button>
              </TftTip>
              <TextFormatFloatingMenu
                open={flipOpen}
                anchorRef={flipBtnRef}
                onClose={() => setFlipOpen(false)}
                className="tft-menu tft-menu--flip tft-menu--floating"
                style={{ minWidth: 188, width: "max-content", maxWidth: "none" }}
              >
                <ul role="menu" aria-label="Flip">
                  <li>
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={Boolean(value.flipH)}
                      className={`tft-menu__item${
                        value.flipH ? " is-active" : ""
                      }`}
                      onClick={() => {
                        patch({ flipH: !value.flipH });
                        setFlipOpen(false);
                      }}
                    >
                      <FlipHIcon />
                      <span className="tft-menu__flip-label">Flip horizontal</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={Boolean(value.flipV)}
                      className={`tft-menu__item${
                        value.flipV ? " is-active" : ""
                      }`}
                      onClick={() => {
                        patch({ flipV: !value.flipV });
                        setFlipOpen(false);
                      }}
                    >
                      <FlipVIcon />
                      <span className="tft-menu__flip-label">Flip vertical</span>
                    </button>
                  </li>
                </ul>
              </TextFormatFloatingMenu>
            </div>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.EFFECT) ? (
            <div className="tft-group">
              <TftTip label="Effect">
                <button
                  ref={effectBtnRef}
                  type="button"
                  className={`tft-toggle-btn tft-position-btn${
                    effectOpen ? " is-open" : ""
                  }${
                    value.effect && value.effect !== "none" ? " is-active" : ""
                  }`}
                  disabled={disabled}
                  aria-label="Effect"
                  aria-expanded={effectOpen}
                  aria-haspopup="menu"
                  onClick={() => {
                    closeMenus("effect");
                    setEffectOpen((v) => !v);
                  }}
                >
                  Effect
                </button>
              </TftTip>
              <TextFormatFloatingMenu
                open={effectOpen}
                anchorRef={effectBtnRef}
                onClose={() => setEffectOpen(false)}
                className="tft-menu tft-menu--flip tft-menu--floating"
                style={{ minWidth: 140, width: "max-content", maxWidth: "none" }}
              >
                <ul role="menu" aria-label="Effect">
                  {IMAGE_EFFECT_OPTIONS.map((opt) => {
                    const active = value.effect === opt.id;
                    return (
                      <li key={opt.id}>
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={active}
                          className={`tft-menu__item${
                            active ? " is-active" : ""
                          }`}
                          onClick={() => {
                            patch({ effect: active ? "none" : opt.id });
                            setEffectOpen(false);
                          }}
                        >
                          <span className="tft-menu__flip-label">{opt.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </TextFormatFloatingMenu>
            </div>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.OPACITY) ? (
            <div className="tft-group tft-opacity">
              <TftTip label="Opacity">
                <button
                  ref={opacityBtnRef}
                  type="button"
                  className={`tft-toggle-btn tft-opacity-btn${
                    opacityOpen ? " is-open" : ""
                  }`}
                  disabled={disabled}
                  aria-label="Transparency"
                  aria-expanded={opacityOpen}
                  onClick={() => {
                    closeMenus("opacity");
                    setOpacityOpen((v) => !v);
                  }}
                >
                  <span className="tft-opacity-icon" aria-hidden="true" />
                </button>
              </TftTip>
              <TextFormatFloatingMenu
                open={opacityOpen}
                anchorRef={opacityBtnRef}
                onClose={() => setOpacityOpen(false)}
                align="center"
                className="tft-popover tft-popover--opacity tft-menu--floating"
              >
                <div className="tft-slider-field">
                  <div className="tft-slider-field__head">
                    <span className="tft-slider-field__label">Opacity</span>
                    <div className="tft-slider-field__value-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="tft-slider-field__input"
                        value={Math.round(Number(value.opacity) || 0)}
                        disabled={disabled}
                        aria-label="Opacity value"
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) {
                            patch({
                              opacity: Math.max(0, Math.min(100, n)),
                            });
                          }
                        }}
                      />
                      <span className="tft-slider-field__suffix">%</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    className="tft-slider"
                    min={0}
                    max={100}
                    step={1}
                    value={Number(value.opacity) || 0}
                    disabled={disabled}
                    aria-label="Opacity"
                    onChange={(e) =>
                      patch({ opacity: Number(e.target.value) })
                    }
                  />
                </div>
              </TextFormatFloatingMenu>
            </div>
          ) : null}

          {has(IMAGE_FORMAT_TOOLS.POSITION) && useLeftPanelOverlay ? (
            <TftTip label="Position">
              <button
                type="button"
                className={`tft-toggle-btn tft-position-btn${
                  positionActive ? " is-open" : ""
                }`}
                disabled={disabled}
                aria-label="Position"
                aria-expanded={positionActive}
                onClick={() => {
                  closeMenus("position");
                  if (!overlayCtx.isPositionOpen) {
                    overlayCtx.openOverlay(OVERLAY_POSITION);
                  }
                }}
              >
                Position
              </button>
            </TftTip>
          ) : null}
        </div>
      </div>
    </TftTipContext.Provider>
  );
};

export default ImageFormatToolbar;
